import {useState, React} from "react";
import {
    Box,
    Divider,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Chip,
    MenuItem,
    Link,
    Typography,
    Grid,
    Tooltip,
    Hidden,
    Button,
} from "@mui/material";
import {ExpandMore, FileDownload} from "@mui/icons-material";
import DoughnutChart from "./ResultsChart";
import DetailModal from "../../CourseTemplateDetails/DetailModal";
import UserTable from "./EmployeeDetailTable";
import "../../../index.css";
import {GetCourseResults} from "../../../api/endpoints";
import {formatNorwegianDate} from "../../../utils/dataUtils";
import {truncate} from "../../../utils/stringUtils";
import apiClient from "../../../api/apiClient";

/** @typedef {import('../../../Dtos/CourseOverviewResponseDto').CourseOverviewResponseDto} CourseOverviewResponseDto */
/** @typedef {import('../../../Dtos/CourseOverviewResponseDto').CountsDto} CountsDto */
/** @typedef {import('../../../Dtos/CourseOverviewResponseDto').UserStatusResponseDto} UserStatusResponseDto */

/**
 * @param {{ resultData: CourseOverviewResponseDto[] }} props
 */
const ResultsTable = ({resultData}) => {
    const [open, setOpen] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [accOpen, setAccOpen] = useState(false);
    const XLSX = require("xlsx");

    const handleAccordionClick = () => {
        setAccOpen(true);
    };

    const handleOpen = (courseId) => {
        setSelectedCourseId(courseId);
        setOpen(true);
    };

    /**
     * @param {UserStatusResponseDto[]} users
     */
    const anyUniqueIdentifiers = (users) => {
        return users?.some(user => user.uniqueIdentifier != null);
    }

    /**
     * @param {CourseOverviewResponseDto[]} data
     */
    const convertData = (data) => {
        const wb = XLSX.utils.book_new();
        const ws_data = [];

        ws_data.push([
            "Kurs navn",
            "Kurs ansvarlig",
            "Planlagte avdelinger",
            "Kurs moduler",
            "Periode",
            "Gjennomført",
            "Påbegynt",
            "Ikke gjennomført",
            "Godkjent",
            "Ikke godkjent",
        ]);

        ws_data.push(...data.map((item) => [
            item.courseName,
            item.courseOwnerNameSurname,
            item.departments.join(", "),
            item.moduleNames.join(", "),
            `${new Date(item.startDateTime).toLocaleString("no-NO").split(",")[0]} - ${new Date(item.endDateTime).toLocaleString("no-NO").split(",")[0]}`,
            item.counts.doneCount,
            item.counts.startedCount,
            item.counts.notStartedCount,
            item.counts.successCount,
            item.counts.failureCount,
        ]));

        ws_data.push([]);

        const hasUniqueIdentifier = anyUniqueIdentifiers(data.flatMap(item => item.userStatus));
        const headers = ["Navn", "Etternavn", "Avdeling"];

        if (hasUniqueIdentifier)
            headers.push("Ansattnummer");

        headers.push("Sist aktiv", "Riktige besvarelser", "Godkjent", "Status", "Sist oppdatert");
        ws_data.push(headers);

        const formatDate = (dateString) =>
            dateString ? new Date(dateString).toLocaleDateString("no-NO") : "-";

        data.forEach((item) => {
            /** @type {UserStatusResponseDto[]} */
            const users = item.userStatus;
            users.forEach((user) => {
                const row = [user.name, user.surname, user.departmentName];

                if (hasUniqueIdentifier)
                    row.push(user.uniqueIdentifier || "-");

                row.push(
                    formatDate(user.lastActiveDateTime),
                    user.score != null ? (user.score * 100).toFixed(2) + "%" : "-",
                    user.isApproved ? "Ja" : "Nei",
                    user.status,
                    formatDate(user.lastActiveDateTime)
                );

                ws_data.push(row);
            });
        });

        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        XLSX.utils.book_append_sheet(wb, ws, 'KursResultatOversikt');
        return wb;
    };

    const exportCourseResult = async (groupId) => {
        try {
            const response = await apiClient.post(GetCourseResults, {groupId});
            /** @type {CourseOverviewResponseDto[]} */
            const data = response.data.data;
            const wb = convertData(data);
            XLSX.writeFile(wb, "course-result.xlsx");
        } catch (error) {
            console.error("Error exporting course result:", error.response);
        }
    };

    function RowItem({ fullData, data, label, icon: Icon, statusColor, subcontent }) {
        return (
            <>
                <MenuItem sx={{fontWeight: "600", color: "#858585"}}>
                    {label}
                </MenuItem>
                <Box sx={{display: "flex", alignItems: "center"}}>
                    <Tooltip
                        title={fullData}
                        placement="top"
                        PopperProps={{
                            modifiers: [{name: "offset", options: {offset: [-5, 23]}}],
                        }}
                    >
                        <MenuItem>{data}</MenuItem>
                    </Tooltip>
                </Box>
                {subcontent && (
                    <Link
                        sx={{fontSize: 13, textDecoration: "none", cursor: "pointer", color: "primary", ml: 1.5}}
                        onClick={handleOpen}
                    >
                        {subcontent}
                    </Link>
                )}
            </>
        );
    }

    const currentDate = new Date();

    /** @type {CourseOverviewResponseDto[]} */
    const sortedResultData = [...resultData].sort((a, b) => {
        const differenceA = Math.abs(currentDate - new Date(a.endDateTime));
        const differenceB = Math.abs(currentDate - new Date(b.endDateTime));

        if (a.isActive && !b.isActive) return -1;
        if (!a.isActive && b.isActive) return 1;
        return differenceA - differenceB;
    });

    return (
        <>
            {sortedResultData.map((course, i) => (
                <Grid container sx={{my: 5}} key={i}>
                    <Grid
                        item xs={12} md={12} lg={12} xl={10}
                        onClick={() => setSelectedCourseId(course.courseId)}
                    >
                        <Box sx={{p: {xs: 2, sm: 2, md: 2}, border: "1px solid #e0e0e0", borderRadius: 2, backgroundColor: "#fff"}}>
                            <Grid container sx={{mb: 1}}>
                                <Grid item xs={4} sm={5}>
                                    <Chip
                                        label={course.isActive ? "Aktiv" : "Inaktiv"}
                                        sx={{backgroundColor: course.isActive ? "#00c853" : "#bdbdbd", color: "#fff"}}
                                    />
                                </Grid>
                                <Grid item xs={4} sm={4}>
                                    <Box sx={{display: "flex", alignItems: "center"}}>
                                        <Typography className="container-header">
                                            {course.courseName} -{" "}
                                            {new Date(course.startDateTime).toLocaleString("no-NO").split(",")[0]}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={3} sm={3} sx={{display: "flex", justifyContent: "flex-end"}}>
                                    <Button sx={{color: "primary"}} onClick={() => exportCourseResult(course.groupId)}>
                                        <FileDownload fontSize="small" sx={{mr: 1}}/>
                                        Eksportér
                                    </Button>
                                </Grid>
                            </Grid>

                            <Grid container sx={{my: 2}}>
                                <Grid item xs={8} sm={7} md={5} lg={3} xl={3}>
                                    <DoughnutChart
                                        chartData={[
                                            ["Course", "Status"],
                                            ["Ikke gjennomført", course.counts.notStartedCount || 0.0000000001],
                                            ["Ikke godkjent",    course.counts.failureCount   || 0.00000001],
                                            ["Påbegynt",         course.counts.startedCount   || 0.00000000001],
                                            ["Godkjent",         course.counts.successCount   || 0.00000000001],
                                        ]}
                                    />
                                </Grid>
                                <Hidden lgDown>
                                    <Divider orientation="vertical" flexItem sx={{height: 65}}/>
                                </Hidden>

                                <Grid item xs={8} sm={5} md={4} lg={2.5} xl={2.5}>
                                    <RowItem
                                        label="Avdeling"
                                        fullData={course.departments.join(", ")}
                                        data={truncate(course.departments.join(", "), 20)}
                                    />
                                </Grid>
                                <Hidden lgDown>
                                    <Divider orientation="vertical" flexItem sx={{height: 65}}/>
                                </Hidden>

                                <Grid item xs={8} sm={4} md={3} lg={2.5} xl={2}>
                                    <RowItem
                                        label="Kursansvarlig"
                                        data={truncate(course.courseOwnerNameSurname, 16)}
                                        fullData={course.courseOwnerNameSurname}
                                    />
                                </Grid>
                                <Hidden lgDown>
                                    <Divider orientation="vertical" flexItem sx={{height: 65}}/>
                                </Hidden>

                                <Grid item xs={8} sm={4} md={5} lg={2.5} xl={2.5}>
                                    <RowItem
                                        label="Moduler"
                                        data={truncate(course.moduleNames.join(", "), 21)}
                                        fullData={course.moduleNames.join(", ")}
                                        subcontent={"Se mer info"}
                                    />
                                </Grid>
                                <Hidden lgDown>
                                    <Divider orientation="vertical" flexItem sx={{height: 65}}/>
                                </Hidden>

                                <Grid item xs={8} sm={3} md={1.5} lg={1} xl={1}>
                                    <RowItem
                                        label="Periode"
                                        fullData={
                                            new Date(course.startDateTime).toLocaleDateString("no-No") +
                                            ` - ` +
                                            new Date(course.endDateTime).toLocaleDateString("no-No")
                                        }
                                        data={`${formatNorwegianDate(new Date(course.startDateTime))} - ${formatNorwegianDate(new Date(course.endDateTime))}`}
                                    />
                                </Grid>
                            </Grid>

                            <Accordion onClick={handleAccordionClick} sx={{mt: 1}}>
                                <AccordionSummary
                                    expandIcon={<ExpandMore/>}
                                    sx={{"& .MuiAccordionSummary-content": {flexGrow: 0}}}
                                />
                                <AccordionDetails sx={{px: 0, py: 1}}>
                                    <UserTable userList={course.userStatus}/>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    </Grid>
                </Grid>
            ))}
            <DetailModal
                onClose={() => setOpen(false)}
                open={open}
                courseId={selectedCourseId}
            />
        </>
    );
};

export default ResultsTable;